import { useCallback, useEffect, useRef, useState } from "react";
import { Bot, Loader2, MessageSquareText, RotateCcw, Send, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/assistant`;
const STORAGE_KEY = "ramalive-assistente-sessao";

const WELCOME = {
  id: "welcome",
  role: "assistant",
  content:
    "Olá! Sou a assistente virtual da RAMALIVE ODONTOLOGIA. Posso tirar dúvidas sobre serviços, horários e como solicitar um agendamento. Como posso ajudar?",
};

const newSessionId = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `s-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

/** Le um stream SSE e chama onEvent(eventName, dataObj) para cada evento. */
async function readSSE(response, onEvent) {
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let sep;
    while ((sep = buffer.indexOf("\n\n")) !== -1) {
      const raw = buffer.slice(0, sep);
      buffer = buffer.slice(sep + 2);
      let event = "message";
      let data = "";
      raw.split("\n").forEach((line) => {
        if (line.startsWith("event:")) event = line.slice(6).trim();
        else if (line.startsWith("data:")) data += line.slice(5).trim();
      });
      if (data) {
        try {
          onEvent(event, JSON.parse(data));
        } catch {
          /* ignora linhas malformadas */
        }
      }
    }
  }
}

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([WELCOME]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [sessionId, setSessionId] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) || newSessionId();
    } catch {
      return newSessionId();
    }
  });
  const [unread, setUnread] = useState(false);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, sessionId);
    } catch {
      /* armazenamento indisponivel */
    }
  }, [sessionId]);

  // Carrega sugestoes e historico salvo
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [infoRes, histRes] = await Promise.all([
          fetch(`${API}/info`),
          fetch(`${API}/history/${sessionId}`),
        ]);
        if (infoRes.ok) {
          const info = await infoRes.json();
          if (!cancelled) setSuggestions(info.suggestions || []);
        }
        if (histRes.ok) {
          const hist = await histRes.json();
          if (!cancelled && hist.messages?.length) {
            setMessages([WELCOME, ...hist.messages.map((m) => ({ id: m.id, role: m.role, content: m.content }))]);
          }
        }
      } catch {
        /* assistente offline: mantem apenas boas-vindas */
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (open) {
      setUnread(false);
      bottomRef.current?.scrollIntoView({ behavior: "smooth" });
      setTimeout(() => inputRef.current?.focus(), 150);
    }
  }, [open, messages.length]);

  const send = useCallback(
    async (text) => {
      const content = text.trim();
      if (!content || sending) return;
      setInput("");
      setSending(true);
      const userMsg = { id: `u-${Date.now()}`, role: "user", content };
      const assistantId = `a-${Date.now()}`;
      setMessages((m) => [...m, userMsg, { id: assistantId, role: "assistant", content: "", pending: true }]);

      const patchAssistant = (fn) =>
        setMessages((m) => m.map((msg) => (msg.id === assistantId ? fn(msg) : msg)));

      try {
        const res = await fetch(`${API}/chat`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ session_id: sessionId, message: content }),
        });
        if (!res.ok || !res.body) throw new Error("http");

        await readSSE(res, (event, data) => {
          if (event === "session" && data.session_id && data.session_id !== sessionId) {
            setSessionId(data.session_id);
          } else if (event === "delta") {
            patchAssistant((msg) => ({ ...msg, content: msg.content + (data.content || ""), pending: false }));
          } else if (event === "done") {
            patchAssistant((msg) => ({ ...msg, content: data.content || msg.content, pending: false }));
            if (!open) setUnread(true);
          } else if (event === "error") {
            patchAssistant((msg) => ({ ...msg, content: data.message, pending: false, error: true }));
          }
        });
      } catch {
        patchAssistant((msg) => ({
          ...msg,
          content: "Não consegui responder agora. Tente novamente em instantes ou fale conosco pelo WhatsApp.",
          pending: false,
          error: true,
        }));
      } finally {
        setSending(false);
      }
    },
    [sending, sessionId, open]
  );

  const reset = async () => {
    try {
      await fetch(`${API}/history/${sessionId}`, { method: "DELETE" });
    } catch {
      /* ignora */
    }
    setMessages([WELCOME]);
    setSessionId(newSessionId());
  };

  const onSubmit = (e) => {
    e.preventDefault();
    send(input);
  };

  const showSuggestions = messages.length <= 1 && suggestions.length > 0;

  return (
    <>
      {/* Painel do chat */}
      <div
        data-testid="chat-widget-panel"
        role="dialog"
        aria-label="Assistente virtual da RAMALIVE"
        aria-hidden={!open}
        className={cn(
          "fixed bottom-24 right-4 z-[var(--z-floating)] flex w-[calc(100vw-2rem)] max-w-[400px] flex-col overflow-hidden rounded-[var(--radius)] border border-border bg-card/95 shadow-[0_30px_80px_rgba(20,16,12,0.22)] backdrop-blur-xl transition-[opacity,transform] duration-200 sm:bottom-24 sm:right-6",
          open ? "translate-y-0 opacity-100" : "pointer-events-none translate-y-3 opacity-0"
        )}
        style={{ height: "min(560px, calc(100vh - 8rem))" }}
      >
        <div className="flex items-center justify-between gap-3 border-b border-border bg-[hsl(var(--brand-off-white))] px-4 py-3">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[hsl(var(--brand-beige))] text-[hsl(var(--brand-soft-brown))]">
              <Bot className="h-5 w-5" />
            </div>
            <div className="leading-tight">
              <p className="font-heading text-base font-semibold text-foreground">Assistente RAMALIVE</p>
              <p className="text-xs text-muted-foreground">Tira dúvidas · não confirma horários</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={reset}
              aria-label="Nova conversa"
              data-testid="chat-reset-button"
              className="h-9 w-9 rounded-lg"
            >
              <RotateCcw className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => setOpen(false)}
              aria-label="Fechar assistente"
              data-testid="chat-close-button"
              className="h-9 w-9 rounded-lg"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <ScrollArea className="flex-1 px-4 py-4">
          <ul className="flex flex-col gap-3" data-testid="chat-messages-list" aria-live="polite">
            {messages.map((m) => (
              <li
                key={m.id}
                data-testid={`chat-message-${m.role}`}
                className={cn("flex", m.role === "user" ? "justify-end" : "justify-start")}
              >
                <div
                  className={cn(
                    "max-w-[85%] whitespace-pre-wrap rounded-2xl px-4 py-2.5 text-sm leading-relaxed",
                    m.role === "user"
                      ? "rounded-br-md bg-primary text-primary-foreground"
                      : m.error
                        ? "rounded-bl-md border border-[hsl(var(--destructive))]/40 bg-background text-foreground"
                        : "rounded-bl-md border border-border bg-background text-foreground"
                  )}
                >
                  {m.pending ? (
                    <span className="inline-flex items-center gap-2 text-muted-foreground" data-testid="chat-typing-indicator">
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      Digitando...
                    </span>
                  ) : (
                    m.content
                  )}
                </div>
              </li>
            ))}
          </ul>

          {showSuggestions && (
            <div className="mt-4 flex flex-wrap gap-2" data-testid="chat-suggestions">
              {suggestions.map((s, i) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => send(s)}
                  data-testid={`chat-suggestion-${i}`}
                  className="rounded-full border border-border bg-background px-3 py-1.5 text-xs font-medium text-foreground transition-colors duration-200 hover:border-[hsl(var(--brand-champagne-gold))] hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {s}
                </button>
              ))}
            </div>
          )}
          <div ref={bottomRef} />
        </ScrollArea>

        <form onSubmit={onSubmit} className="flex items-center gap-2 border-t border-border bg-card px-3 py-3" data-testid="chat-form">
          <Input
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Escreva sua dúvida..."
            maxLength={1500}
            disabled={sending}
            aria-label="Mensagem para a assistente"
            data-testid="chat-input"
            className="h-11 flex-1 rounded-xl bg-background"
          />
          <Button
            type="submit"
            size="icon"
            disabled={sending || !input.trim()}
            aria-label="Enviar mensagem"
            data-testid="chat-send-button"
            className="h-11 w-11 shrink-0 rounded-xl"
          >
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </Button>
        </form>
        <p className="border-t border-border bg-[hsl(var(--brand-off-white))] px-4 py-2 text-[11px] leading-snug text-muted-foreground">
          Respostas geradas por IA e podem conter imprecisões. Horários são confirmados apenas pela equipe.
        </p>
      </div>

      {/* Botao flutuante */}
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              aria-label={open ? "Fechar assistente virtual" : "Abrir assistente virtual"}
              aria-expanded={open}
              data-testid="chat-widget-toggle-button"
              className="fixed bottom-4 right-[5.25rem] z-[var(--z-floating)] flex h-14 w-14 items-center justify-center rounded-2xl border border-[hsl(var(--brand-champagne-gold))]/50 bg-card text-foreground shadow-[0_14px_40px_rgba(20,16,12,0.18)] transition-[background-color,transform] duration-200 hover:bg-accent active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background sm:bottom-6 sm:right-[5.75rem]"
            >
              {open ? <X className="h-6 w-6" /> : <MessageSquareText className="h-6 w-6" />}
              {unread && !open && (
                <span
                  aria-hidden="true"
                  className="absolute -right-1 -top-1 h-3 w-3 rounded-full bg-[hsl(var(--brand-champagne-gold))] ring-2 ring-card"
                />
              )}
            </button>
          </TooltipTrigger>
          <TooltipContent side="left" className="rounded-xl border-border bg-card text-foreground">
            Assistente virtual
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </>
  );
};
